/**
 * TaskSchedulerPage
 * Admin/supervisor view of all tasks grouped by workflow instance.
 * Supports view mode toggling, status filtering, and inline task creation.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CalendarDays, Plus, RefreshCw, AlertCircle } from 'lucide-react';
import {
  SummaryCard,
  SummaryCardsRow,
  ViewToggle,
  TaskDetailModal,
  type ViewToggleOption,
} from '../../components/tasks';
import { useTaskList, useTaskSummary } from '../../hooks/useTasks';
import { useTaskUIStore } from '../../stores/taskUIStore';
import { useAuthStore } from '../../stores/authStore';
import { theme } from '../../styles/theme';
import type { Task, TaskType, WorkflowInstanceSummary } from '../../types/tasks';
import { SchedulerFilterTabs } from './SchedulerFilterTabs';
import { SchedulerNewTaskForm } from './SchedulerNewTaskForm';
import { WorkflowTaskList, type WorkflowGroup } from './WorkflowTaskList';

// ============================================================================
// Constants
// ============================================================================

const VIEW_OPTIONS: ViewToggleOption[] = [
  { key: 'workflow', label: 'By Workflow' },
  { key: 'client', label: 'By Client' },
  { key: 'date', label: 'By Date' },
  { key: 'type', label: 'By Type' },
];

const ACCENT = {
  // Indigo — not in the theme palette; keeping hex
  total: '#6366f1',
  // Amber — theme.colors.status.warning.text is #d97706; using it for consistency
  pending: theme.colors.status.warning.text,
  // Blue (info-like) — not in the theme palette; keeping hex
  inProgress: '#3b82f6',
  // Red — theme.colors.status.error.text is #dc2626; using it for consistency
  overdue: theme.colors.status.error.text,
  // Orange — not in the theme palette; keeping hex
  sla: '#f97316',
} as const;

// ============================================================================
// Styled components
// ============================================================================

const PageWrapper = styled.main`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.status.info.bg};
  color: ${({ theme }) => theme.colors.status.info.text};
  flex-shrink: 0;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const NewTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.white};
  background: ${({ theme }) => theme.colors.primary.base};
  border: 1px solid ${({ theme }) => theme.colors.primary.base};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
    border-color: ${({ theme }) => theme.colors.primary.dark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const PaginationInfo = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin: 0;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

// ---- Loading skeleton ----

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SkeletonCard = styled.div`
  height: 68px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.backgrounds.gray} 25%,
    ${({ theme }) => theme.colors.backgrounds.light} 50%,
    ${({ theme }) => theme.colors.backgrounds.gray} 75%
  );
  background-size: 400% 100%;
  animation: shimmer 1.6s ease-in-out infinite;

  @keyframes shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

// ---- Error state ----

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing[12]} ${theme.spacing[4]}`};
  gap: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const ErrorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const ErrorTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const ErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 400px;
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.white};
  background: ${({ theme }) => theme.colors.status.error.text};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.status.error.text};
    outline-offset: 2px;
  }
`;

// ============================================================================
// Helpers
// ============================================================================

const STANDALONE_INSTANCE: WorkflowInstanceSummary = {
  id: 'standalone',
  workflowType: 'Ad Hoc',
  status: 'ACTIVE',
  slaDeadline: null,
  clientMember: null,
  assignedTo: null,
};

function groupTasksByWorkflow(tasks: Task[]): WorkflowGroup[] {
  const map = new Map<string, WorkflowGroup>();

  for (const task of tasks) {
    const key = task.workflowInstanceId ?? 'standalone';
    if (!map.has(key)) {
      map.set(key, {
        workflowInstance: task.workflowInstance ?? { ...STANDALONE_INSTANCE, id: key },
        tasks: [],
      });
    }
    map.get(key)!.tasks.push(task);
  }

  return Array.from(map.values());
}

const TASK_TYPE_LABELS: Record<TaskType, string> = {
  CLINICAL_FORM: 'Clinical Form',
  REVIEW: 'Review',
  MEETING: 'Meeting',
  AD_HOC: 'Ad Hoc',
};

function groupTasksByType(tasks: Task[]): WorkflowGroup[] {
  const map = new Map<TaskType, WorkflowGroup>();

  for (const task of tasks) {
    if (!map.has(task.type)) {
      const syntheticInstance: WorkflowInstanceSummary = {
        id: `type-${task.type}`,
        workflowType: TASK_TYPE_LABELS[task.type],
        status: 'ACTIVE',
        slaDeadline: null,
        clientMember: null,
        assignedTo: null,
      };
      map.set(task.type, { workflowInstance: syntheticInstance, tasks: [] });
    }
    map.get(task.type)!.tasks.push(task);
  }

  // Render in a stable, predictable order matching the TaskType union
  const typeOrder: TaskType[] = ['CLINICAL_FORM', 'REVIEW', 'MEETING', 'AD_HOC'];
  return typeOrder.flatMap((t) => (map.has(t) ? [map.get(t)!] : []));
}

function groupTasksByClient(tasks: Task[]): WorkflowGroup[] {
  const map = new Map<string, WorkflowGroup>();

  for (const task of tasks) {
    const client = task.workflowInstance?.clientMember;
    const key = client?.id ?? 'unassigned';
    const label = client ? `${client.firstName} ${client.lastName}` : 'Unassigned Client';

    if (!map.has(key)) {
      map.set(key, {
        workflowInstance: {
          id: `client-${key}`,
          workflowType: label,
          status: 'ACTIVE',
          slaDeadline: null,
          clientMember: client ?? null,
          assignedTo: null,
        },
        tasks: [],
      });
    }
    map.get(key)!.tasks.push(task);
  }

  // Sort alphabetically by client name, with unassigned last
  return Array.from(map.values()).sort((a, b) => {
    if (a.workflowInstance.id === 'client-unassigned') return 1;
    if (b.workflowInstance.id === 'client-unassigned') return -1;
    return a.workflowInstance.workflowType.localeCompare(b.workflowInstance.workflowType);
  });
}

function groupTasksByDate(tasks: Task[]): WorkflowGroup[] {
  const map = new Map<string, WorkflowGroup>();

  for (const task of tasks) {
    const dateStr = task.scheduledDate;
    const key = dateStr ? dateStr.slice(0, 10) : 'no-date';
    const label = key === 'no-date'
      ? 'No Scheduled Date'
      : new Date(key + 'T00:00:00').toLocaleDateString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

    if (!map.has(key)) {
      map.set(key, {
        workflowInstance: {
          id: `date-${key}`,
          workflowType: label,
          status: 'ACTIVE',
          slaDeadline: null,
          clientMember: null,
          assignedTo: null,
        },
        tasks: [],
      });
    }
    map.get(key)!.tasks.push(task);
  }

  // Sort chronologically, no-date last
  return Array.from(map.values()).sort((a, b) => {
    if (a.workflowInstance.id === 'date-no-date') return 1;
    if (b.workflowInstance.id === 'date-no-date') return -1;
    return a.workflowInstance.id.localeCompare(b.workflowInstance.id);
  });
}

// ============================================================================
// Sub-component: loading skeleton
// ============================================================================

function LoadingSkeleton() {
  return (
    <SkeletonWrapper aria-busy="true" aria-label="Loading tasks...">
      {[1, 2, 3].map((i) => (
        <SkeletonCard key={i} />
      ))}
    </SkeletonWrapper>
  );
}

// ============================================================================
// Main page component
// ============================================================================

export function TaskSchedulerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    searchParams.get('taskId'),
  );

  // Keep the modal in sync when the URL ?taskId= param changes after mount
  // (e.g. when a notification deep-link is clicked while already on this page).
  useEffect(() => {
    setSelectedTaskId(searchParams.get('taskId'));
  }, [searchParams]);

  const {
    schedulerViewMode,
    schedulerFormOpen,
    schedulerStatusFilter,
    setSchedulerViewMode,
    setSchedulerStatusFilter,
    toggleSchedulerForm,
  } = useTaskUIStore();

  const hasPermission = useAuthStore((state) => state.hasPermission);

  const { data: summary } = useTaskSummary();

  const listParams = useMemo(
    () => ({
      status: schedulerStatusFilter !== 'ALL' ? schedulerStatusFilter : undefined,
      limit: 100,
      sortBy: 'sortBand' as const,
      sortOrder: 'asc' as const,
    }),
    [schedulerStatusFilter],
  );

  const { data: taskList, isLoading, isError, refetch } = useTaskList(listParams);

  const workflowGroups = useMemo<WorkflowGroup[]>(() => {
    if (!taskList?.data) return [];
    switch (schedulerViewMode) {
      case 'type':
        return groupTasksByType(taskList.data);
      case 'client':
        return groupTasksByClient(taskList.data);
      case 'date':
        return groupTasksByDate(taskList.data);
      case 'workflow':
      default:
        return groupTasksByWorkflow(taskList.data);
    }
  }, [taskList?.data, schedulerViewMode]);

  // Build count map for filter tabs
  const statusCounts = useMemo(() => {
    if (!summary) return undefined;
    return {
      ALL: summary.total,
      PENDING: summary.byStatus.PENDING,
      IN_PROGRESS: summary.byStatus.IN_PROGRESS,
      COMPLETED: summary.byStatus.COMPLETED,
      OVERDUE: summary.byStatus.OVERDUE,
    };
  }, [summary]);

  return (
    <PageWrapper>
      {/* ── Page header ── */}
      <PageHeader>
        <HeaderLeft>
          <HeaderIcon aria-hidden="true">
            <CalendarDays size={22} />
          </HeaderIcon>
          <TitleGroup>
            <PageTitle>Task Scheduler</PageTitle>
            <PageSubtitle>Manage and track tasks across all active Red Dots</PageSubtitle>
          </TitleGroup>
        </HeaderLeft>

        {/* Only render the New Task button when the user has task:create permission */}
        {hasPermission('tasks:my_tasks') && (
          <NewTaskButton onClick={toggleSchedulerForm} type="button" aria-label="Open new task form">
            <Plus size={15} aria-hidden="true" />
            New Task
          </NewTaskButton>
        )}
      </PageHeader>

      {/* ── Inline create form ── */}
      {schedulerFormOpen && <SchedulerNewTaskForm />}

      {/* ── Summary metric cards ── */}
      <SummaryCardsRow>
        <SummaryCard
          label="Total Tasks"
          value={summary?.total ?? 0}
          accentColor={ACCENT.total}
          onClick={() => setSchedulerStatusFilter('ALL')}
        />
        <SummaryCard
          label="Pending"
          value={summary?.byStatus?.PENDING ?? 0}
          accentColor={ACCENT.pending}
          onClick={() => setSchedulerStatusFilter('PENDING')}
        />
        <SummaryCard
          label="In Progress"
          value={summary?.byStatus?.IN_PROGRESS ?? 0}
          accentColor={ACCENT.inProgress}
          onClick={() => setSchedulerStatusFilter('IN_PROGRESS')}
        />
        <SummaryCard
          label="Overdue"
          value={summary?.byStatus?.OVERDUE ?? 0}
          accentColor={ACCENT.overdue}
          onClick={() => setSchedulerStatusFilter('OVERDUE')}
        />
        <SummaryCard
          label="Timeline Warnings"
          value={summary?.byEscalationTier?.WARNING ?? 0}
          accentColor={ACCENT.sla}
        />
      </SummaryCardsRow>

      {/* ── View mode + filter toolbar ── */}
      <ToolbarRow>
        <ViewToggle
          options={VIEW_OPTIONS}
          activeKey={schedulerViewMode}
          onChange={(key) => setSchedulerViewMode(key as typeof schedulerViewMode)}
        />
      </ToolbarRow>

      <SchedulerFilterTabs
        activeFilter={schedulerStatusFilter}
        onFilterChange={setSchedulerStatusFilter}
        statusCounts={statusCounts}
      />

      {/* ── Task list area ── */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : isError ? (
        <ErrorCard role="alert">
          <ErrorIconWrapper aria-hidden="true">
            <AlertCircle size={28} />
          </ErrorIconWrapper>
          <ErrorTitle>Failed to load tasks</ErrorTitle>
          <ErrorMessage>
            There was a problem fetching tasks. Please try again.
          </ErrorMessage>
          <RetryButton type="button" onClick={() => refetch()}>
            <RefreshCw size={14} aria-hidden="true" />
            Retry
          </RetryButton>
        </ErrorCard>
      ) : (
        <>
          <WorkflowTaskList
            groups={workflowGroups}
            onViewDetails={(taskId) => setSelectedTaskId(taskId)}
          />
          {taskList?.meta && taskList.meta.totalPages > 1 && (
            <PaginationInfo>
              Showing {taskList.data.length} of {taskList.meta.total} tasks
              {/* TODO: Add full pagination controls */}
            </PaginationInfo>
          )}
        </>
      )}

      {/* Task detail modal — opened via onViewDetails on each task row, or ?taskId= deep-link */}
      {selectedTaskId && (
        <TaskDetailModal
          taskId={selectedTaskId}
          onClose={() => {
            setSelectedTaskId(null);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete('taskId');
              return next;
            });
          }}
        />
      )}
    </PageWrapper>
  );
}

export default TaskSchedulerPage;
